import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApolloQueryResult } from '@apollo/client';
import { IOnmComparisonEntity, INamespaceOnm } from 'src/models/comparison';
import { IGranularity } from 'src/models/general';
import { IClipping } from 'src/models/onlineMedia';

interface IDataSelected {
  id: string;
  name: string;
}

export interface IOnmComparison {
  comparisonId: string;
  comparisonList: ApolloQueryResult<Array<INamespaceOnm>>;
  comparisonEntity: ApolloQueryResult<IOnmComparisonEntity>;
  brandPositioning: ApolloQueryResult<any>;
  shareOfVoice: ApolloQueryResult<any>;
  sentimentAnalysis: ApolloQueryResult<any>;
  dayToDayMedia: ApolloQueryResult<any>;
  dayToDayArticle: ApolloQueryResult<any>;
  advanceMetric: ApolloQueryResult<any>;
  mediaGranularity: IGranularity;
  articleGranularity: IGranularity;
  selectedClipping: Array<IClipping>;
  selectedLabel: Array<IDataSelected>;
}

export interface IHeadCellAdvanceMetrics {
  disablePadding: boolean;
  id: any;
  label: string;
  numeric: boolean;
  size: number;
  active: boolean;
}

const initGranularity: IGranularity = { unit: 'day', value: 1 };

export const initHeadCells: Array<IHeadCellAdvanceMetrics> = [
  {
    id: 'totalArticle',
    numeric: false,
    disablePadding: true,
    label: 'Total Article',
    size: 10,
    active: false
  },
  {
    id: 'totalMedia',
    numeric: true,
    disablePadding: true,
    label: 'Total Media',
    size: 10,
    active: false
  },
  {
    id: 'prValue',
    numeric: true,
    disablePadding: true,
    label: 'PR Value',
    size: 10,
    active: false
  },
  {
    id: 'sentimentPositive',
    numeric: true,
    disablePadding: true,
    label: 'Positive',
    size: 10,
    active: false
  },
  {
    id: 'sentimentNeutral',
    numeric: true,
    disablePadding: true,
    label: 'Neutral',
    size: 10,
    active: false
  },
  {
    id: 'sentimentNegative',
    numeric: true,
    disablePadding: true,
    label: 'Negative',
    size: 10,
    active: false
  }
];

const initialState: IOnmComparison = {
  comparisonId: '',
  comparisonList: {
    errors: undefined,
    loading: false,
    networkStatus: 1,
    partial: false,
    data: []
  },
  comparisonEntity: {
    errors: undefined,
    loading: true,
    networkStatus: 1,
    partial: false,
    data: undefined
  },
  brandPositioning: {
    errors: undefined,
    loading: false,
    networkStatus: 1,
    partial: false,
    data: []
  },
  shareOfVoice: {
    errors: undefined,
    loading: false,
    networkStatus: 1,
    partial: false,
    data: []
  },
  advanceMetric: {
    errors: undefined,
    loading: false,
    networkStatus: 1,
    partial: false,
    data: []
  },
  dayToDayMedia: {
    errors: undefined,
    loading: false,
    networkStatus: 1,
    partial: false,
    data: []
  },
  dayToDayArticle: {
    errors: undefined,
    loading: false,
    networkStatus: 1,
    partial: false,
    data: []
  },
  sentimentAnalysis: {
    errors: undefined,
    loading: false,
    networkStatus: 1,
    partial: false,
    data: []
  },
  mediaGranularity: initGranularity,
  articleGranularity: initGranularity,
  selectedClipping: [],
  selectedLabel: []
};

export const onmComparison = createSlice({
  name: 'onmComparison',
  initialState,
  reducers: {
    reducerUpdateOnmComparisonList: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<Array<INamespaceOnm>>>
    ) => {
      state.comparisonList = action.payload;
    },
    reducerUpdateOnmComparisonEntity: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<IOnmComparisonEntity>>
    ) => {
      state.comparisonEntity = action.payload;
    },
    reducerUpdateAllLoadingComparison: (
      state: IOnmComparison,
      action: PayloadAction<boolean>
    ) => {
      state.brandPositioning.loading = action.payload;
      state.shareOfVoice.loading = action.payload;
      state.advanceMetric.loading = action.payload;
      state.dayToDayMedia.loading = action.payload;
      state.dayToDayArticle.loading = action.payload;
      state.sentimentAnalysis.loading = action.payload;
    },
    reducerUpdateBrandPosition: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<Array<any>>>
    ) => {
      state.brandPositioning = action.payload;
    },
    reducerUpdateShareOfVoice: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<Array<any>>>
    ) => {
      state.shareOfVoice = action.payload;
    },
    reducerUpdateAdvanceMetric: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<Array<any>>>
    ) => {
      state.advanceMetric = action.payload;
    },
    reducerUpdateDayToDayMedia: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<Array<any>>>
    ) => {
      state.dayToDayMedia = action.payload;
    },
    reducerUpdateDayToDayArticle: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<Array<any>>>
    ) => {
      state.dayToDayArticle = action.payload;
    },
    reducerUpdateSentimentAnalysis: (
      state: IOnmComparison,
      action: PayloadAction<ApolloQueryResult<Array<any>>>
    ) => {
      state.sentimentAnalysis = action.payload;
    },
    reducerUpdateMediaGranularity: (
      state: IOnmComparison,
      action: PayloadAction<IGranularity>
    ) => {
      state.mediaGranularity = action.payload;
    },
    reducerUpdateArticleGranularity: (
      state: IOnmComparison,
      action: PayloadAction<IGranularity>
    ) => {
      state.articleGranularity = action.payload;
    },
    reducerUpdateDataSelectedLabel: (
      state: IOnmComparison,
      action: PayloadAction<Array<IDataSelected>>
    ) => {
      state.selectedLabel = action.payload;
    },
    reducerUpdateDataSelectedClipping: (
      state: IOnmComparison,
      action: PayloadAction<Array<IClipping>>
    ) => {
      state.selectedClipping = action.payload;
    }
  }
});

export const {
  reducerUpdateOnmComparisonList,
  reducerUpdateOnmComparisonEntity,
  reducerUpdateAllLoadingComparison,
  reducerUpdateBrandPosition,
  reducerUpdateShareOfVoice,
  reducerUpdateAdvanceMetric,
  reducerUpdateDayToDayMedia,
  reducerUpdateDayToDayArticle,
  reducerUpdateSentimentAnalysis,
  reducerUpdateMediaGranularity,
  reducerUpdateArticleGranularity,
  reducerUpdateDataSelectedLabel,
  reducerUpdateDataSelectedClipping
} = onmComparison.actions;

export default onmComparison.reducer;
